import { action, observable, runInAction } from 'mobx';
import { ErrorInterface } from 'modules/core/api/globalApi';
import controller from '../controller';

/** Элемент списка подразделений партнёра (GET /v1/partner_division);
 *  он же — элемент списка подразделений аутентифицированного пользователя (GET /v1/partner_division_users). */
export interface DivisionItemInterface {
  id: string | number,
  name?: string,
  email?: string,
  comment?: string,
  partnerId?: string,
  partnerName?: string,
  createdDate?: number,
}

/** Привязка пользователя к подразделению (GET /v1/partner_division_users/{userId}):
 *  все подразделения партнёра с признаком привязки у пользователя. `updated` клиентский —
 *  панель привязок помечает изменённые чекбоксы до сохранения. */
export interface UserDivisionItemInterface {
  divisionId: string,
  name?: string,
  available?: boolean,
  primary?: boolean,
  updated?: boolean,
}

/** Сотрудник подразделения (GET /v1/partner_division/{divisionId}/users). */
export interface DivisionUserItemInterface {
  userId: string,
  userName?: string,
  divisionId?: string,
  primary?: boolean,
}

/** Коллега аутентифицированного пользователя (GET /v1/partner_division_users/colleagues). */
export interface UserColleagueItemInterface {
  userId: string,
  userFullName?: string,
  userEmail?: string,
}

export interface PartnerDivisionStore {
  divisionsList: DivisionItemInterface[],
  // ключ кэша = partnerId последнего успешного запроса или 'all'; null — данных ещё не было
  divisionsRequest: string | null,

  loadingDivisions: boolean,

  // блок привязки пользователей к подразделениям (бывш. Redux-слайс partnerDivision)
  /** подразделения пользователя userDivisionsUserId с признаками привязки (бывш. userDivisionsList) */
  userDivisions: UserDivisionItemInterface[],
  /** userId, по которому загружен userDivisions; null — данных ещё не было (бывш. userDivisionId) */
  userDivisionsUserId: string | null,
  /** подразделения аутентифицированного пользователя (бывш. divisionsByUserList) */
  authUserDivisions: DivisionItemInterface[],
  authUserDivisionsLoaded: boolean,
  /** сотрудники подразделения usersByDivisionId (бывш. usersByDivisionsList) */
  usersByDivision: DivisionUserItemInterface[],
  /** divisionId, по которому загружен usersByDivision (бывш. selectedDivisionId) */
  usersByDivisionId: string | null,
  /** коллеги подразделений аутентифицированного пользователя (бывш. userDivisionsColleagues) */
  userDivisionsColleagues: UserColleagueItemInterface[],
  userDivisionsColleaguesLoaded: boolean,
  loadingUserDivisionsColleagues: boolean,

  error: ErrorInterface,
}

const initialValues: PartnerDivisionStore = {
  divisionsList: [],
  divisionsRequest: null,

  loadingDivisions: false,

  userDivisions: [],
  userDivisionsUserId: null,
  authUserDivisions: [],
  authUserDivisionsLoaded: false,
  usersByDivision: [],
  usersByDivisionId: null,
  userDivisionsColleagues: [],
  userDivisionsColleaguesLoaded: false,
  loadingUserDivisionsColleagues: false,

  error: null,
};

export const partnerDivisionStore = observable(initialValues);

// запросы в полёте (не обзерваблы: на рендер не влияют) — single-flight на дублирующие mount'ы
let divisionsInFlight: string | null = null;
let userDivisionsInFlight: string | null = null;
let authUserDivisionsInFlight = false;
let usersByDivisionInFlight: string | null = null;
let colleaguesInFlight = false;

export const partnerDivisionActions = {

  handleError: action((response) => {
    if (response.errorData) {
      const { errorData } = response;
      partnerDivisionStore.error = {
        message: errorData?.message,
        errorCode: errorData?.errorCode,
        timestamp: errorData?.timestamp,
        status: response?.status,
        connectError: response?.connectError,
        isError: response?.error,
      };
      throw errorData;
    }
  }),

  async clearError() {
    partnerDivisionStore.error = null;
  },

  // GET /v1/partner_division?partnerId= — подразделения партнёра (DivisionSelector)
  // или все подразделения ('all', страница «Подразделения»).
  // force=true обходит кэш: страница справочника перечитывает список после create/update/delete.
  async getDivisions(partnerId?: string, force = false) {
    const requestKey = partnerId || 'all';
    if (!force && (requestKey === partnerDivisionStore.divisionsRequest || requestKey === divisionsInFlight)) {
      return;
    }
    divisionsInFlight = requestKey;
    runInAction(() => {
      // чистим синхронно, чтобы не мигать списком другого партнёра при смене partnerId
      partnerDivisionStore.divisionsList = [];
      partnerDivisionStore.loadingDivisions = true;
    });
    try {
      const response = await controller.getPartnerDivision(partnerId);

      partnerDivisionActions.handleError(response);
      runInAction(() => {
        // ключ фиксируем только при успехе: при ошибке следующий mount повторит запрос
        partnerDivisionStore.divisionsRequest = requestKey;
        partnerDivisionStore.divisionsList = response?.data ?? [];
      });
    } catch (err) {
      this.handleError(err);
    } finally {
      divisionsInFlight = null;
      runInAction(() => {
        partnerDivisionStore.loadingDivisions = false;
      });
    }
  },

  clearDivisions: action(() => {
    partnerDivisionStore.divisionsList = [];
    partnerDivisionStore.divisionsRequest = null;
    partnerDivisionStore.error = null;
  }),

  // CRUD справочника подразделений (POST/PUT/DELETE /v1/partner_division) — модалка
  // редактора подразделений (бывш. Redux-танки create/update/deletePartnerDivision).
  // Кэш divisionsList не трогаем: страница справочника после CRUD перечитывает его
  // через getDivisions(undefined, true). При ошибке handleError пишет error в стор
  // и бросает исключение — модалка показывает toastr и ошибки в форму.
  async createDivision(params: Record<string, unknown>): Promise<void> {
    const response = await controller.createPartnerDivision(params);
    partnerDivisionActions.handleError(response);
  },

  async updateDivision(params: Record<string, unknown>): Promise<void> {
    const response = await controller.updatePartnerDivision(params);
    partnerDivisionActions.handleError(response);
  },

  async deleteDivision(divisionId: string | number): Promise<void> {
    const response = await controller.deletePartnerDivision(divisionId);
    partnerDivisionActions.handleError(response);
  },

  // GET /v1/partner_division_users/{userId} — все подразделения партнёра с признаком
  // привязки у пользователя (панель привязок в карточке пользователя).
  // Кэш на сессию по userId: карточка одного пользователя перезагружается без повторного
  // запроса; force=true перечитывает после изменения привязок (UserEdit после сохранения).
  async getPartnerDivisionUser(userId: string, force = false) {
    if (!force && (userId === partnerDivisionStore.userDivisionsUserId
      || userId === userDivisionsInFlight)) {
      return;
    }
    userDivisionsInFlight = userId;
    try {
      const response = await controller.getPartnerDivisionUser(userId);

      partnerDivisionActions.handleError(response);
      runInAction(() => {
        partnerDivisionStore.userDivisionsUserId = userId;
        partnerDivisionStore.userDivisions = response?.data ?? [];
      });
    } catch (err) {
      this.handleError(err);
    } finally {
      userDivisionsInFlight = null;
    }
  },

  clearPartnerDivisionUser: action(() => {
    partnerDivisionStore.userDivisions = [];
    partnerDivisionStore.userDivisionsUserId = null;
    partnerDivisionStore.error = null;
  }),

  // оптимистичная запись локального состояния панели привязок (чекбоксы до сохранения;
  // бывш. updatePartnerDivisionUserInRedux) — FormContext формы читает её при ре-рендере
  setUserDivisions: action((userDivisions: UserDivisionItemInterface[]) => {
    partnerDivisionStore.userDivisions = userDivisions;
  }),

  // привязка/отвязка/главное подразделение (POST/PUT/DELETE /v1/partner_division_users).
  // Кэш userDivisions не трогаем: карточка пользователя после сохранения перечитывает его
  // через getPartnerDivisionUser(userId, true). При ошибке — error в стор + исключение.
  async createPartnerDivisionUser(params: { userId: string, divisionId: string }): Promise<void> {
    const response = await controller.createPartnerDivisionUser(params);
    partnerDivisionActions.handleError(response);
  },

  async putPartnerDivisionUser(params: { userId: string, divisionId: string }): Promise<void> {
    const response = await controller.putPartnerDivisionUser(params);
    partnerDivisionActions.handleError(response);
  },

  async deletePartnerDivisionUser(params: { userId: string, divisionId: string }): Promise<void> {
    const response = await controller.deletePartnerDivisionUser(params);
    partnerDivisionActions.handleError(response);
  },

  // GET /v1/partner_division_users — подразделения аутентифицированного пользователя
  // (выбор подразделения в комментариях заявки и в модалке ответственных).
  // Кэш на сессию (в Redux качалось заново на каждый mount); force=true перечитывает.
  async getPartnerDivisionByAuth(force = false) {
    if (!force && (partnerDivisionStore.authUserDivisionsLoaded || authUserDivisionsInFlight)) {
      return;
    }
    authUserDivisionsInFlight = true;
    try {
      const response = await controller.getPartnerDivisionByAuth();

      partnerDivisionActions.handleError(response);
      runInAction(() => {
        partnerDivisionStore.authUserDivisionsLoaded = true;
        partnerDivisionStore.authUserDivisions = response?.data ?? [];
      });
    } catch (err) {
      this.handleError(err);
    } finally {
      authUserDivisionsInFlight = false;
    }
  },

  // GET /v1/partner_division/{divisionId}/users — сотрудники подразделения.
  // Один слот под последний divisionId (как в Redux): несколько селекторов с разными
  // подразделениями на одной странице будут перезапрашивать список — так было и раньше.
  // force=true перечитывает; clearUsersByDivision сбрасывает слот при размонтировании.
  async getUsersByDivision(data: { divisionId?: string, onlyPrimary?: boolean }, force = false) {
    const { divisionId } = data;
    if (!divisionId || (!force && (divisionId === partnerDivisionStore.usersByDivisionId
      || divisionId === usersByDivisionInFlight))) {
      return;
    }
    usersByDivisionInFlight = divisionId;
    try {
      const response = await controller.getUsersByDivision(divisionId, data.onlyPrimary);

      partnerDivisionActions.handleError(response);
      runInAction(() => {
        partnerDivisionStore.usersByDivisionId = divisionId;
        partnerDivisionStore.usersByDivision = response?.data ?? [];
      });
    } catch (err) {
      this.handleError(err);
    } finally {
      usersByDivisionInFlight = null;
    }
  },

  clearUsersByDivision: action(() => {
    partnerDivisionStore.usersByDivision = [];
    partnerDivisionStore.usersByDivisionId = null;
    partnerDivisionStore.error = null;
  }),

  // GET /v1/partner_division_users/colleagues — коллеги подразделений аутентифицированного
  // пользователя (UserSelector с showColleagues, комментарии заявки). Один запрос за сессию:
  // в Redux оба потребителя перезапрашивали на каждый mount, хотя состав коллег меняется
  // крайне редко; force=true перечитывает. primary фильтрует только «главные» привязки.
  async getAuthUserDivisionsColleagues(primary?: boolean, force = false) {
    if (!force && (partnerDivisionStore.userDivisionsColleaguesLoaded || colleaguesInFlight)) {
      return;
    }
    colleaguesInFlight = true;
    runInAction(() => {
      partnerDivisionStore.loadingUserDivisionsColleagues = true;
      partnerDivisionStore.userDivisionsColleagues = [];
    });
    try {
      const response = await controller.getAuthUserDivisionsColleagues(primary);

      partnerDivisionActions.handleError(response);
      runInAction(() => {
        partnerDivisionStore.userDivisionsColleaguesLoaded = true;
        partnerDivisionStore.userDivisionsColleagues = response?.data ?? [];
      });
    } catch (err) {
      this.handleError(err);
    } finally {
      colleaguesInFlight = false;
      runInAction(() => {
        partnerDivisionStore.loadingUserDivisionsColleagues = false;
      });
    }
  },

};
